import {PosOrderSummary} from "@/data/entities/order";
import {FulfillmentFilter} from "@widgets/pos/orders/sidebar/fulfillment/fulfillmentFilterButtons";

export const normalize = (value: string): string =>
    value
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "") // é -> e, ı stays
        .toLowerCase();

/**
 * Digits only, without leading 0 or 31 country code, so "06 1234" matches
 * "+31 6 12345678".
 */
const phoneDigits = (value: string): string =>
    value.replace(/\D/g, "").replace(/^(0031|31|0)/, "");

/** Looks like (part of) a phone number: only digits, spaces and + ( ) - . */
const isPhoneLike = (value: string): boolean => /^[\d\s()+\-.]+$/.test(value);

/** At least this many digits to search on the phone number. */
const MIN_PHONE_DIGITS = 3;

/**
 * Whether an order matches the search:
 *
 * - Every term is found in the name, account, email address, address or order
 *   number; or
 * - The query as a whole looks like a phone number (spaces allowed, e.g.
 *   "06 - 12 34 56 78") and its digits are in the phone number of the order.
 */
export const matchesSearch = (order: PosOrderSummary, terms: string[], query: string): boolean => {
    const text = normalize([
        order.firstName,
        order.lastName,
        order.account,
        order.emailAddress,
        order.street,
        order.streetNumber,
        order.city,
        order.cartId,
    ].filter(v => v !== null && v !== undefined).join(" "));

    if (terms.every(term => text.includes(term)))
        return true;

    // Phone number: optional for orders via the POS
    if (order.phoneNumber === null || !isPhoneLike(query))
        return false;

    const digits = phoneDigits(query);
    return digits.length >= MIN_PHONE_DIGITS && phoneDigits(order.phoneNumber).includes(digits);
};

type FilterOrdersArgs = {
    orders: PosOrderSummary[],
    fulfillmentFilter: FulfillmentFilter,
    searchQuery: string,
};

export const filterOrders = ({orders, searchQuery, fulfillmentFilter}: FilterOrdersArgs) => {
    const query: string = normalize(searchQuery).trim();
    const terms: string[] = query.split(/\s+/).filter(t => t.length > 0);

    return orders.filter((order: PosOrderSummary) => {
        // Filter out on fulfillment
        if (fulfillmentFilter === "TAKEAWAY" && order.delivery)
            return false;

        if (fulfillmentFilter === "DELIVERY" && !order.delivery)
            return false;

        // Match search
        return terms.length === 0 || matchesSearch(order, terms, query);
    });
};